/**
 * 一番賞底部操作列的文案與控制項狀態。
 *
 * 排隊狀態機不在這裡，一律吃 queue-state.mjs 的 queueViewState() 結論——
 * 底部列只負責把那邊的結論翻成畫面。
 */

import { drawGateState } from './queue-state.mjs';
import { formatCountdown, JOINING_TEXT } from './info-bar-state.mjs';

/** 第一行最多列幾個號碼，其餘收成 `+N`；桌機寬得多，列得完整些。 */
const DESKTOP_MAX_SHOWN = 5;
const MOBILE_MAX_SHOWN = 2;

/** 只有還握著隊列位置的人才需要離開排隊。 */
const HOLDING_A_PLACE_STATES = ['in_queue', 'summoned', 'active'];

/** 回傳的字串會直接進 DOM，所有嵌入值先轉成數字，字串型 post meta 不會原樣穿透。 */
function num(value) {
    const n = Number(value);

    return Number.isFinite(n) ? n : 0;
}

function bold(value) {
    return `<b>${num(value)}</b>`;
}

function joinParts(parts) {
    return parts.filter(Boolean).join(' · ');
}

/**
 * 已選號碼清單，依 selection 的順序（即點選順序）列出，號碼補零到三位；超過 maxShown 只列前幾個並附省略數。
 *
 * @param {number[]} selection
 * @param {number} maxShown
 * @returns {string}
 */
export function selectedNumbersLabel(selection, maxShown) {
    const numbers = (selection || []).map(num);
    if (numbers.length === 0) {
        return '';
    }

    const shown = numbers.slice(0, maxShown).map((n) => String(n).padStart(3, '0')).join('、');
    const omitted = numbers.length - maxShown;

    return omitted > 0 ? `${shown} +${omitted}` : shown;
}

/**
 * 狀態列兩行。底部列＝我的動作，所以只講已選號碼與合計；
 * 籤別與等待人數是頁面狀態，歸資訊列。
 *
 * @param {{selection: number[], totalPrice: (number|string|null), layout: ('mobile'|'desktop')}} input
 * @returns {{lead: string, sub: string}}
 */
export function barStatusLines({ selection, totalPrice, layout }) {
    const picked = selection || [];
    if (picked.length === 0) {
        return { lead: '請先選擇號碼', sub: '' };
    }

    const maxShown = layout === 'desktop' ? DESKTOP_MAX_SHOWN : MOBILE_MAX_SHOWN;

    return {
        lead: joinParts([`已選 ${bold(picked.length)} 號`, selectedNumbersLabel(picked, maxShown)]),
        // 金額缺價格時整段省略，不顯示 NT$NaN
        sub: totalPrice === null || totalPrice === undefined ? '' : `合計 <b>NT$${num(totalPrice)}</b>`,
    };
}

/**
 * 單一主按鈕欄位。加入排隊與確認開獎在時間上互斥，所以底部列只留一個欄位輪流承載。
 *
 * `interactive` 為 false 時渲染成非 button；順位變動只換文字、不換節點型別，版面才不會跳動。
 *
 * @param {object|null|undefined} view queueViewState() 的結論
 * @param {number[]} selection
 * @returns {{kind: ('join'|'waiting'|'draw'|'rejoin'), label: string, disabled: boolean, interactive: boolean}}
 */
export function primarySlotState(view, selection) {
    const draw = {
        kind: 'draw',
        label: '確認開獎',
        disabled: (selection || []).length === 0,
        interactive: true,
    };

    if (!view || !view.hasQueue) {
        return draw;
    }

    switch (view.state) {
        case 'in_queue':
            return {
                kind: 'waiting',
                label: `排隊中 · 第 ${num(view.position)} 位`,
                disabled: true,
                interactive: false,
            };
        // 叫號中會自動回應並立即轉為輪到你，另做一套外觀只會讓畫面閃一下
        case 'summoned':
        case 'active':
            return draw;
        case 'kicked':
            // 過號者要的是「再排一次」，靜靜變回「加入排隊」等於沒告訴他發生了什麼
            return { kind: 'rejoin', label: '重新排隊', disabled: false, interactive: true };
        default:
            return { kind: 'join', label: '加入排隊', disabled: false, interactive: true };
    }
}

/** 不能選號的原因逐態各異，一句通用文案會在排隊中與過號兩態說錯話。 */
const PICKING_BLOCKED_HINTS = {
    not_in_queue: '排隊後才能選號',
    in_queue: '輪到你才能選號',
    kicked: '重新排隊後才能選號',
};

/**
 * 選號工具與號碼盤的停用狀態。還沒輪到你就不能選號，清除隨之一起關掉——
 * 留著一顆能按的清除鍵，等於讓停用中的盤面還有一個可變動的入口。
 *
 * @param {{view: object|null, selection: number[]}} input
 * @returns {{
 *   toolsDisabled: boolean, boardInteractive: boolean,
 *   clearDisabled: boolean, drawReason: (string|null), hintText: (string|null),
 *   leaveVisible: boolean, timerText: string
 * }}
 */
export function barControlState({ view, selection }) {
    const gate = drawGateState(view);

    return {
        toolsDisabled: gate.disabled,
        boardInteractive: !gate.disabled,
        clearDisabled: gate.disabled || (selection || []).length === 0,
        drawReason: gate.reason,
        hintText: gate.disabled ? (PICKING_BLOCKED_HINTS[view?.state] ?? null) : null,
        leaveVisible: Boolean(view?.hasQueue) && HOLDING_A_PLACE_STATES.includes(view.state),
        timerText: view?.isJoining ? JOINING_TEXT : formatCountdown(view?.remainingSeconds ?? null),
    };
}
